import type { PostgrestError } from '@supabase/supabase-js';

/**
 * Exhaustively fetch every row of a PostgREST query by paging through it.
 *
 * PostgREST (Supabase) caps EVERY response at 1,000 rows by default — an
 * unbounded `.select()` doesn't error past the cap, it silently returns the
 * first 1,000 rows. That broke the check-ins reports in July 2026 when
 * `check_ins` crossed 1,000 rows, and every other raw-row report fetch has the
 * same cliff waiting as its table grows. Any query whose row count can grow
 * without bound MUST go through this helper (or a server-side aggregate RPC,
 * which is preferable when the client only needs the aggregate — see
 * 20260704020000_check_in_report_aggregates.sql).
 *
 * How it pages: every page re-issues the caller's query ordered by a stable
 * unique column (`id` by default) so ORDER-BY ties can't skip or duplicate
 * rows at page boundaries. If the caller's query carries its own `.order()`,
 * ours is appended as a tiebreaker, preserving the caller's ordering. A short
 * page (< pageSize) marks the last page.
 *
 * Consistency caveat: this is offset pagination, not a snapshot. The default
 * `id` columns are random uuids (not monotonic), so a row inserted or deleted
 * by a concurrent writer mid-pagination shifts later windows and can duplicate
 * or drop one boundary row for that single read. Reports tolerate this — they
 * refetch on a staleTime — but don't build mutations on top of this helper.
 *
 * The result mirrors the `{ data, error }` shape of a plain supabase query so
 * call sites keep their existing error-handling conventions; unlike a plain
 * query, `data` is always an array. On a page error, `data` is `[]` — never a
 * silently partial result. A malformed page (no rows AND no error) throws
 * rather than being mistaken for the end of the data.
 *
 * Works for set-returning RPCs too (they're subject to the same cap):
 * `fetchAllRows(() => supabase.rpc('fn'))`, provided the returned TABLE has a
 * stable unique column to order by.
 *
 * @param buildPage returns a FRESH query (builders are single-use) WITHOUT
 *   `.range()`; filters and `.order()` calls are fine.
 */
export async function fetchAllRows<T>(
  buildPage: () => PageableQuery,
  {
    pageSize = 1000,
    maxPages = 500,
    orderColumn = 'id',
  }: { pageSize?: number; maxPages?: number; orderColumn?: string } = {},
): Promise<{ data: T[]; error: PostgrestError | null }> {
  // A pageSize above the server's max-rows cap (1,000 on Supabase) can never
  // be honored: PostgREST clips every response, and a clipped page would
  // masquerade as the terminal short page — silent truncation again.
  const size = Math.min(pageSize, 1000);
  const rows: T[] = [];
  for (let page = 0; page < maxPages; page++) {
    const from = page * size;
    const { data, error } = await buildPage()
      .order(orderColumn, { ascending: true })
      .range(from, from + size - 1);

    if (error) return { data: [], error };
    if (data == null) {
      // A 200 with neither rows nor an error is a malformed page (proxy /
      // SDK edge case) — treating it as end-of-data would silently truncate.
      throw new Error('fetchAllRows: page returned neither rows nor an error');
    }

    const pageRows = data as T[];
    rows.push(...pageRows);
    if (pageRows.length < size) return { data: rows, error: null };
  }
  // No silent caps: past maxPages (default 500k rows) something is wrong —
  // a non-terminating range or a table far beyond this helper's design point.
  throw new Error(
    `fetchAllRows: exceeded ${maxPages} pages of ${pageSize} rows — refusing to return a silently truncated result`,
  );
}

/** The minimal structural slice of a PostgREST query builder the helper needs. */
interface PageableQuery {
  order(
    column: string,
    options: { ascending: boolean },
  ): {
    range(
      from: number,
      to: number,
    ): PromiseLike<{ data: unknown; error: PostgrestError | null }>;
  };
}
